import React from "react";
import { Checkbox, Card, Badge } from "antd";
import ModalContainer from "../ModalContainer.tsx";
import {
  createModulesPermission,
  getModulesPermissionByPartnerId,
  upsertModulesPermission,
  applyModulesToAll,
  getReleasedAll,
  revertReleasedAll,
  type ModulesPermission,
  type CreateModulesPermissionDto,
} from "../../services/modules-permissions.service.ts";
import { autocompletePartners } from "../../services/partner.service.ts";
import { NOMINA_MODULES } from "../../constants/nomina-modules.ts";
import { notification } from "../../utils/Notifications.tsx";

interface AddModulesAppPermissionsProps {
  closeFn: () => void;
  setLoading: (val: boolean) => void;
}

interface Partner {
  id: number;
  person: {
    firstName: string;
    lastName: string;
    secondLastName?: string;
  };
}

const AddModulesAppPermissions = ({
  closeFn,
  setLoading,
}: AddModulesAppPermissionsProps) => {
  const [partners, setPartners] = React.useState([] as Partner[]);
  const [selectedPartner, setSelectedPartner] = React.useState<number | null>(
    null
  );
  const [searchPartner, setSearchPartner] = React.useState<string>("");
  const [filteredPartners, setFilteredPartners] = React.useState<Partner[]>([]);
  const [showPartnerDropdown, setShowPartnerDropdown] = React.useState(false);
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [selectedModules, setSelectedModules] = React.useState<string[]>([]);
  const [currentPermissions, setCurrentPermissions] =
    React.useState<ModulesPermission | null>(null);
  const [applyingAll, setApplyingAll] = React.useState<boolean>(false);
  const [releasedAll, setReleasedAll] = React.useState<boolean>(false);
  const [releasedModules, setReleasedModules] = React.useState<string[]>([]);
  const [showRevertModal, setShowRevertModal] = React.useState<boolean>(false);
  const [showConfirmReleaseModal, setShowConfirmReleaseModal] = React.useState<boolean>(false);

  let timeoutId: any;

  const loadPartnerPermissions = async (partnerId: number) => {
    try {
      setLoading(true);
      const resp = await getModulesPermissionByPartnerId(partnerId);
      setCurrentPermissions(resp);
      setSelectedModules(resp?.modules || []);
    } catch (error) {
      console.error("Error loading partner permissions:", error);
      setCurrentPermissions(null);
      setSelectedModules([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPartners = async (searchTerm: string = "") => {
    try {
      setSearchLoading(true);
      const resp = await autocompletePartners(searchTerm);
      setPartners(resp || []);
      setFilteredPartners(resp || []);
    } catch (error) {
      console.error("Error fetching partners:", error);
    } finally {
      setSearchLoading(false);
    }
  };

  const handlePartnerSearch = async (searchTerm: string) => {
    setSearchPartner(searchTerm);
    setShowPartnerDropdown(true);

    if (timeoutId) clearTimeout(timeoutId);

    timeoutId = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const data = await autocompletePartners(searchTerm);
        setFilteredPartners(data);
      } catch (error) {
        console.error("Error searching partners:", error);
      } finally {
        setSearchLoading(false);
      }
    }, 500);
  };

  React.useEffect(() => {
    fetchPartners();
    // cargar bandera global
    (async () => {
      try {
        const data = await getReleasedAll();
        setReleasedAll(Boolean(data?.released));
        setReleasedModules(data?.modules || []);
      } catch {}
    })();
  }, []);

  const handleSavePermissions = async () => {
    if (!selectedPartner) {
      notification(
        "Error",
        "Debes seleccionar un colaborador",
        "danger"
      );
      return;
    }

    try {
      setLoading(true);
      const data: CreateModulesPermissionDto = {
        partnerId: selectedPartner,
        modules: selectedModules,
      };

      await upsertModulesPermission(data);
      notification(
        "Éxito",
        "Permisos de módulos actualizados correctamente",
        "success"
      );

      await loadPartnerPermissions(selectedPartner);
    } catch (error) {
      notification("Error", "No se pudieron guardar los permisos", "danger");
    } finally {
      setLoading(false);
    }
  };

  const handleModuleChange = (moduleKey: string, checked: boolean) => {
    if (checked) {
      setSelectedModules([...selectedModules, moduleKey]);
    } else {
      setSelectedModules(selectedModules.filter((key) => key !== moduleKey));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedModules(NOMINA_MODULES.map((module) => module.key));
    } else {
      setSelectedModules([]);
    }
  };

  const handleClearAll = () => {
    setSelectedPartner(null);
    setSearchPartner("");
    setSelectedModules([]);
    setCurrentPermissions(null);
    setShowPartnerDropdown(false);
    setFilteredPartners([]);
  };

  const handlePartnerSelect = (partner: Partner) => {
    setSelectedPartner(partner.id);
    setSearchPartner(`${partner.person.firstName} ${partner.person.lastName}`);
    setShowPartnerDropdown(false);

    loadPartnerPermissions(partner.id);
  };

  const handleApplyAllPartners = async () => {
    if (releasedAll) {
      setShowRevertModal(true);
      return;
    }
    
    if (selectedModules.length === 0) {
      notification("Error", "Debes seleccionar al menos un módulo para liberar", "danger");
      return;
    }
    
    setShowConfirmReleaseModal(true);
  };

  const handleConfirmRelease = async () => {
    try {
      setApplyingAll(true);
      setLoading(true);
      const res = await applyModulesToAll(selectedModules);
      setReleasedAll(res?.released || false);
      setReleasedModules(res?.modules || []);
      notification("Éxito", `Módulos liberados globalmente (${(res?.modules || []).length})`, "success");
      setShowConfirmReleaseModal(false);
    } catch (error) {
      notification("Error", "No se pudo liberar globalmente", "danger");
    } finally {
      setApplyingAll(false);
      setLoading(false);
    }
  };

  const handleConfirmRevert = async () => {
    try {
      setApplyingAll(true);
      setLoading(true);
      const res = await revertReleasedAll();
      setReleasedAll(res?.released || false);
      setReleasedModules(res?.modules || []);
      setSelectedModules([]); // Limpiar los checks
      setShowRevertModal(false);
      notification("Éxito", "Liberación global revertida", "success");
    } catch (error) {
      notification("Error", "No se pudo revertir la liberación", "danger");
    } finally {
      setApplyingAll(false);
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="row g-2 mb-3">
        <div className="col-12">
          <label className="form-label">Colaborador</label>
          <div className="position-relative">
            <div className="input-group">
              <div
                className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 start-0"
                style={{ zIndex: 10 }}
              >
                {searchLoading ? (
                  <i className="fa fa-spinner fa-spin" />
                ) : (
                  <i className="fa fa-search opacity-5" />
                )}
              </div>
              <input
                type="text"
                className="form-control px-35px bg-light"
                placeholder="Buscar colaborador..."
                value={searchPartner}
                onChange={(e) => handlePartnerSearch(e.target.value)}
                onFocus={() => setShowPartnerDropdown(true)}
              />
              {selectedPartner && (
                <div
                  className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 end-0"
                  style={{ zIndex: 10, cursor: 'pointer' }}
                  onClick={handleClearAll}
                  title="Limpiar selección"
                >
                  <i className="fa fa-times text-muted" />
                </div>
              )}
            </div>
            {showPartnerDropdown && filteredPartners.length > 0 && (
              <div
                className="position-absolute w-100 bg-white border rounded shadow-sm"
                style={{
                  zIndex: 1000,
                  maxHeight: "200px",
                  overflowY: "auto",
                }}
              >
                {filteredPartners.map((partner) => (
                  <div
                    key={partner.id}
                    className="px-3 py-2 cursor-pointer hover-bg-light"
                    style={{ cursor: "pointer" }}
                    onClick={() => handlePartnerSelect(partner)}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f8f9fa")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "white")
                    }
                  >
                    {partner.person.firstName} {partner.person.lastName}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-12">
          <Card
            title="Módulos de Nómina"
            size="small"
            extra={
              <div className="d-flex align-items-center gap-2">
                {releasedAll && (
                  <span className="badge bg-success">Modulos liberados globalmente</span>
                )}
                <Checkbox
                  checked={selectedModules.length === NOMINA_MODULES.length}
                  indeterminate={
                    selectedModules.length > 0 &&
                    selectedModules.length < NOMINA_MODULES.length
                  }
                  onChange={(e) => handleSelectAll(e.target.checked)}
                >
                  Seleccionar todos
                </Checkbox>
                <Badge
                  count={selectedModules.length}
                  showZero
                  color="#52c41a"
                />
              </div>
            }
          >
            {releasedAll && (
              <div className="alert alert-info mb-3" role="alert">
                <i className="fa fa-info-circle me-2"></i>
                <strong>Módulos liberados globalmente:</strong> {releasedModules.join(', ')}. 
                Estos módulos se aplicarán automáticamente a todos los colaboradores, 
                combinándose con sus permisos individuales existentes.
              </div>
            )}
            <div className="row">
              {NOMINA_MODULES.map((module) => (
                <div key={module.key} className="col-md-6 col-lg-4 mb-2">
                  <Checkbox
                    checked={selectedModules.includes(module.key)}
                    onChange={(e) =>
                      handleModuleChange(module.key, e.target.checked)
                    }
                  >
                    <i className={`fa fa-${module.icon} me-2`}></i>
                    {module.name}
                    {releasedAll && releasedModules.includes(module.key) && (
                      <span className="badge bg-success ms-2">Global</span>
                    )}
                  </Checkbox>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-12">
          <div
            className={`alert ${selectedPartner ? "alert-info" : "alert-warning"}`}
            role="alert"
          >
            <i className="fa fa-info-circle me-2"></i>
            {selectedPartner ? (
              <>
                <strong>Asignación individual:</strong> Los módulos seleccionados se guardarán para este colaborador específico. 
                {releasedAll && " Los módulos globales se aplicarán automáticamente además de los seleccionados."}
              </>
            ) : (
              <>
                <strong>Liberación global:</strong> Los módulos seleccionados se aplicarán a todos los colaboradores. 
                {releasedAll && " Puedes editar permisos individuales en cualquier momento."}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-between">
        <button
          className="btn btn-outline-primary"
          onClick={handleApplyAllPartners}
          disabled={applyingAll}
        >
          {applyingAll ? (
            <>
              <i className="fa fa-spinner fa-spin me-2"></i>
              {releasedAll ? 'Revirtiendo...' : 'Liberando a todos...'}
            </>
          ) : (
            <>
              <i className="fa fa-users me-2"></i>
              {releasedAll ? 'Revertir liberación' : 'Liberar a todos'}
            </>
          )}
        </button>
        <div className="d-flex gap-2">
          <button className="btn btn-secondary" onClick={closeFn}>
            Cancelar
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSavePermissions}
            disabled={!selectedPartner}
          >
            Guardar Permisos
          </button>
        </div>
      </div>
      <ModalContainer
        open={showRevertModal}
        title="Revertir liberación global"
        closeFn={() => setShowRevertModal(false)}
        children={
          <div>
            <p className="fs-15px mb-3">
              <strong>¿Estás seguro de que deseas revertir la liberación global?</strong>
            </p>
            <p className="fs-14px text-muted mb-3">
              Los siguientes módulos globales serán removidos de todos los colaboradores:
            </p>
            <div className="border rounded p-3 mb-3 bg-light">
              <div className="row">
                {releasedModules.map((moduleKey) => {
                  const module = NOMINA_MODULES.find(m => m.key === moduleKey);
                  return (
                    <div key={moduleKey} className="col-md-6 mb-2">
                      <i className={`fa fa-${module?.icon} me-2 text-danger`}></i>
                      <span className="fw-medium">{module?.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="alert alert-warning" role="alert">
              <i className="fa fa-exclamation-triangle me-2"></i>
              <strong>Importante:</strong> Esta acción removerá los módulos globales de todos los colaboradores.
              Solo se mantendrán los permisos individuales que cada colaborador tenga asignados específicamente.
            </div>
            <div className="d-flex justify-content-end mt-3">
              <button 
                className="btn btn-secondary me-2" 
                onClick={() => setShowRevertModal(false)}
                disabled={applyingAll}
              >
                Cancelar
              </button>
              <button 
                className="btn btn-danger" 
                onClick={handleConfirmRevert}
                disabled={applyingAll}
              >
                {applyingAll ? (
                  <>
                    <i className="fa fa-spinner fa-spin me-2"></i>
                    Revirtiendo...
                  </>
                ) : (
                  <>
                    <i className="fa fa-undo me-2"></i>
                    Revertir Liberación
                  </>
                )}
              </button>
            </div>
          </div>
        }
      />
      
      <ModalContainer
        open={showConfirmReleaseModal}
        title="Confirmar liberación global"
        closeFn={() => setShowConfirmReleaseModal(false)}
        children={
          <div>
            <p className="fs-15px mb-3">
              <strong>¿Estás seguro de que deseas liberar estos módulos globalmente?</strong>
            </p>
            <p className="fs-14px text-muted mb-3">
              Los siguientes módulos se aplicarán a todos los colaboradores, 
              combinándose con sus permisos individuales existentes:
            </p>
            <div className="border rounded p-3 mb-3 bg-light">
              <div className="row">
                {selectedModules.map((moduleKey) => {
                  const module = NOMINA_MODULES.find(m => m.key === moduleKey);
                  return (
                    <div key={moduleKey} className="col-md-6 mb-2">
                      <i className={`fa fa-${module?.icon} me-2 text-primary`}></i>
                      <span className="fw-medium">{module?.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="alert alert-warning" role="alert">
              <i className="fa fa-exclamation-triangle me-2"></i>
              <strong>Importante:</strong> Esta acción afectará a todos los colaboradores del sistema.
              Los módulos liberados se combinarán con los permisos individuales existentes.
            </div>
            <div className="d-flex justify-content-end mt-3">
              <button 
                className="btn btn-secondary me-2" 
                onClick={() => setShowConfirmReleaseModal(false)}
                disabled={applyingAll}
              >
                Cancelar
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleConfirmRelease}
                disabled={applyingAll}
              >
                {applyingAll ? (
                  <>
                    <i className="fa fa-spinner fa-spin me-2"></i>
                    Liberando...
                  </>
                ) : (
                  <>
                    <i className="fa fa-users me-2"></i>
                    Liberar Globalmente
                  </>
                )}
              </button>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default AddModulesAppPermissions;
